import { INSTAGRAM_FACTS_SHIELDED_WHEN_UNAUTH } from '@/components/MediaKit/FavouriteFacts/favouriteFactsDisplay';
import {
  instagramTopPostNeedsAuthForStats,
  parseTopPostBlob
} from '@/components/MediaKit/TopPosts/topPostsUtils';

// ==================== Email Validation ====================
export const isValidEmail = email => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex?.test(email);
};

// ==================== Number Validation ====================
export const isOnlyNumbers = value => /^\d*$/.test(value);

// ==================== Password Validation ====================
export const validatePassword = password => {
  const value = (password ?? '').trim();

  const lengthCheck = value.length >= 8;
  const specialCharCheck = /[!@#$%^&*()_\-+=<>?{}[\]~`|\\:;"',./]/.test(value);
  const capitalCharCheck = /[A-Z]/.test(value);
  const numberCheck = /[0-9]/.test(value);

  if (!lengthCheck) return 'Password must be at least 8 characters long!';
  if (!specialCharCheck) return 'Password must include at least one special character!';
  if (!capitalCharCheck) return 'Password must include at least one uppercase letter!';
  if (!numberCheck) return 'Password must include at least one number!';

  return '';
};

// ==================== Empty Checks ====================
export const isEmpty = obj => {
  return Object.values(obj)?.some(
    value =>
      value === null ||
      value === undefined ||
      (typeof value === 'boolean' && value === false) ||
      (typeof value === 'string' && value.trim() === '') ||
      Number.isNaN(value)
  );
};

export const isEmptyVal = value => !value.trim();

// ==================== URL Validation ====================
export const isHttpProtocol = url => {
  if (!url?.trim()) return true;
  if (url?.slice(0, 4) === 'http') {
    window.open(url, '_blank')
  } else {
    window.open(`https://${url}`, '_blank')
  }
}

export const isValidURL = (url) => {
  if (!url) return false;

  const formattedUrl = url.trim().match(/^https?:\/\//)
    ? url.trim()
    : `https://${url.trim()}`;

  try {
    const parsed = new URL(formattedUrl);
    if (!/^[a-zA-Z0-9]([a-zA-Z0-9.-]*[a-zA-Z0-9])?$/.test(parsed.hostname)) return false;
    // Require a proper TLD: at least 2 labels, last one 2–6 letters (e.g. reject "www.facebook")
    const labels = parsed.hostname.split('.');
    if (labels.length < 2) return false;
    const tld = labels[labels.length - 1];
    if (!/^[a-zA-Z]{2,6}$/.test(tld)) return false;
    return true;
  } catch {
    return false;
  }
};

// ==================== Mandatory Sections Validation ====================
export const checkMandatorySections = (data, isPremiumMediaKit, isUserFree, mkStates) => {

  // MANDATORY SECTIONS
  let topPosts = [];
  let contentPost1;
  let contentPost2;
  let isMandatoryFavFacts;
  // let isMandatoryTopVideos;
  let isMandatoryBrandPartners;

  // SECTIONS DATA
  const favFacts = data?.favFacts;
  const topPostsList = data?.topPosts?.slice(0, -2);
  const topContentPost1 = data?.topPosts[data?.topPosts?.length - 2];
  const topContentPost2 = data?.topPosts[data?.topPosts?.length - 1];

  // const topVideos = data?.favouriteContents;
  const brandPartners = data?.brandPartners;

  // COMPUTATIONS
  // CHECK TOP POSTS
  if (topPostsList?.length > 0 && (mkStates?.topPosts?.isVisibility ?? data?.mediaKit?.isTopPostSectionToggle)) {
    topPostsList?.map((item, index) => {
      const parsedFreePost = parseTopPostBlob(item?.freePost);
      if (parsedFreePost) {
        if (parsedFreePost?.mediaType === 'VIDEO') {
          topPosts?.push(`topPost${index + 1}`);
        }
      }
    });
  }

  // CHECK TOP CONTENT 1
  if (topContentPost1 && (mkStates?.contentPost1?.isVisibility ?? data?.mediaKit?.isContentPost1SectionToggle)) {
    const parsedFreePost = parseTopPostBlob(topContentPost1?.freePost);
    if (parsedFreePost) {
      if (parsedFreePost?.mediaType === 'VIDEO') {
        contentPost1 = true;
      }
    }
  }

  // CHECK TOP CONTENT 2
  if (topContentPost2 && (mkStates?.contentPost2?.isVisibility ?? data?.mediaKit?.isContentPost2SectionToggle)) {
    const parsedFreePost = parseTopPostBlob(topContentPost2?.freePost);
    if (parsedFreePost) {
      if (parsedFreePost?.mediaType === 'VIDEO') {
        contentPost2 = true;
      }
    }
  }

  // CHECK FAV FACTS
  if (favFacts?.length > 0 && isPremiumMediaKit && isUserFree && (mkStates?.favFacts?.isVisibility ?? data?.mediaKit?.isFavFactSectionToggle)) {
    isMandatoryFavFacts = mkStates?.favFacts?.favFacts?.some(item => item?.isPaid);
  }

  // CHECK TOP VIDEOS
  // if (topVideos?.length > 0 && (mkStates?.topVideos?.isVisibility ?? data?.mediaKit?.isSliderVideoSectionToggle)) {
  //   isMandatoryTopVideos = (topVideos?.length > 0 ? true : false) && (mkStates?.topVideos?.isVisibility ?? data?.mediaKit?.isSliderVideoSectionToggle);
  // }

  // CHECK BRAND PARTNERS
  if (brandPartners?.length > 0 && (mkStates?.brandPartners?.isVisibility ?? data?.mediaKit?.isBrandPartnerSectionToggle)) {
    isMandatoryBrandPartners = brandPartners?.length > 0 ? true : false;
  }

  const isMandatory =
    topPosts?.length > 0 ||
    contentPost1 ||
    contentPost2 ||
    isMandatoryFavFacts ||
    // isMandatoryTopVideos ||
    isMandatoryBrandPartners;

  const object = {
    isMandatory: isMandatory || false,
    sections: {
      favFacts: { isMandatory: isMandatoryFavFacts || false, id: 'favourite-facts' },
      // topVideos: { isMandatory: isMandatoryTopVideos || false, id: 'top-videos' },
      brandPartners: { isMandatory: isMandatoryBrandPartners || false, id: 'brand-partners' },
      contentPost1: { isMandatory: contentPost1 || false, id: 'topContent1' },
      contentPost2: { isMandatory: contentPost2 || false, id: 'topContent2' }
    },
    topPosts
  };

  return object;
};

// ==================== Instagram Auth Validation ====================
export const checkMandatoryInstagramAuth = (data, mediaKitData, isPremiumKit) => {
  // MANDATORY SECTIONS
  let instaAuthenticatedIds = [];

  // SECTIONS DATA
  const topPostsList = data?.topPosts?.slice(0, -2);
  const topContentPost1 = data?.topPosts[data?.topPosts?.length - 2];
  const topContentPost2 = data?.topPosts[data?.topPosts?.length - 1];

  // CHECK TOP POSTS
  if (topPostsList?.length > 0 && (mediaKitData?.topPosts?.isVisibility ?? data?.mediaKit?.isTopPostSectionToggle)) {
    topPostsList?.map((item, index) => {
      const post = isPremiumKit
        ? parseTopPostBlob(item?.paidPost)
        : parseTopPostBlob(item?.freePost);
      if (post && instagramTopPostNeedsAuthForStats(post)) {
        instaAuthenticatedIds.push(`topPost${index + 1}`);
      }
    });
  }

  // CHECK TOP CONTENT 1
  if (topContentPost1 && (mediaKitData?.contentPost1?.isVisibility ?? data?.mediaKit?.isContentPost1SectionToggle)) {
    const post = isPremiumKit
      ? parseTopPostBlob(topContentPost1?.paidPost)
      : parseTopPostBlob(topContentPost1?.freePost);

    if (post && instagramTopPostNeedsAuthForStats(post)) {
      instaAuthenticatedIds.push(`topPost6`);
    }
  }

  // CHECK TOP CONTENT 2
  if (topContentPost2 && (mediaKitData?.contentPost2?.isVisibility ?? data?.mediaKit?.isContentPost2SectionToggle)) {
    const post = isPremiumKit
      ? parseTopPostBlob(topContentPost2?.paidPost)
      : parseTopPostBlob(topContentPost2?.freePost);

    if (post && instagramTopPostNeedsAuthForStats(post)) {
      instaAuthenticatedIds.push(`topPost7`);
    }
  }

  const instagramFavFactNeedsAuth = item =>
    item?.socialChannelName === 'Instagram' &&
    INSTAGRAM_FACTS_SHIELDED_WHEN_UNAUTH.includes(item?.factName);

  if (mediaKitData?.favFacts?.favFacts?.length > 0 && (mediaKitData?.favFacts?.isVisibility ?? data?.mediaKit?.isFavFactSectionToggle)) {
    const isFavFactsIncludedInLocal = mediaKitData?.favFacts?.favFacts?.find(instagramFavFactNeedsAuth);

    if (isFavFactsIncludedInLocal) {
      instaAuthenticatedIds.push('favourite-facts');
    }
  } else {
    const isInstaDataIncluded = data?.favFacts?.find(instagramFavFactNeedsAuth);
    if (isInstaDataIncluded) {
      instaAuthenticatedIds.push('favourite-facts');
    }
  }

  if (mediaKitData?.audienceLocation?.isVisibility || data?.mediaKit?.isAudienceLocationIncluded) {
    instaAuthenticatedIds.push('audience-section');
  }

  if (mediaKitData?.verificationBanner?.isVisibility || data?.mediaKit?.isBannerIncluded) {
    instaAuthenticatedIds.push('verification-banner');
  }

  return instaAuthenticatedIds;
};

